import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import useSWR from 'swr';
import {
  mainColors,
  secondaryColors,
  successColors,
  accentColors,
  alertColors
} from '../../theme/themeConfig';
import PartnerPortalApi from '../../api/partnerPortal';
import type { PortalProject } from '../../api/partnerPortal';
import PortalProjectDetail from './PortalProjectDetail';

const TableCard = styled(Box)(() => ({
  background: '#fff',
  border: `1px solid ${secondaryColors.SECONDARY_6}`,
  borderRadius: '12px',
  boxShadow: '0 1px 3px rgba(15,28,46,.06)',
  overflow: 'hidden'
}));

const StyledHeaderCell = styled(TableCell)(() => ({
  background: '#F4F7FB',
  borderBottom: `1px solid ${secondaryColors.SECONDARY_6}`,
  color: secondaryColors.SECONDARY,
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '.6px',
  padding: '10px 16px',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap'
}));

const StyledRow = styled(TableRow)(() => ({
  '&:hover': { background: mainColors.MAIN_LIGHT },
  '&:last-child td': { borderBottom: 0 },
  cursor: 'pointer'
}));

const STATUS_CONFIG: Record<
  string,
  { bg: string; color: string; dot: string }
> = {
  'Cancellation Requested': {
    bg: alertColors.ALERT_6,
    color: alertColors.ALERT,
    dot: alertColors.ALERT
  },
  'Contract Updated': {
    bg: accentColors.ACCENT_6,
    color: accentColors.ACCENT,
    dot: accentColors.ACCENT
  },
  'In Progress': {
    bg: mainColors.MAIN_LIGHT,
    color: mainColors.MAIN,
    dot: mainColors.MAIN
  },
  'On Hold': {
    bg: alertColors.ALERT_6,
    color: alertColors.ALERT,
    dot: alertColors.ALERT
  },
  'Re-schedule': {
    bg: accentColors.ACCENT_6,
    color: accentColors.ACCENT,
    dot: accentColors.ACCENT
  },
  Scheduled: {
    bg: successColors.SUCCESS_6,
    color: successColors.SUCCESS,
    dot: successColors.SUCCESS_7
  }
};

function StatusPill({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] ?? {
    bg: secondaryColors.SECONDARY_6,
    color: secondaryColors.SECONDARY,
    dot: secondaryColors.SECONDARY_1
  };
  return (
    <Box
      sx={{
        alignItems: 'center',
        background: cfg.bg,
        borderRadius: '20px',
        display: 'inline-flex',
        fontSize: '11px',
        fontWeight: 600,
        gap: '5px',
        px: '9px',
        py: '3px',
        whiteSpace: 'nowrap'
      }}
    >
      <Box
        sx={{
          background: cfg.dot,
          borderRadius: '50%',
          flexShrink: 0,
          height: '6px',
          width: '6px'
        }}
      />
      <span style={{ color: cfg.color }}>{status}</span>
    </Box>
  );
}

const formatDate = (d?: string) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const PortalProjects: React.FC = function PortalProjects() {
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    null
  );

  const { data: projects } = useSWR(
    'portal-projects',
    PartnerPortalApi.getProjects
  );

  if (selectedProjectId) {
    return (
      <PortalProjectDetail
        onBack={() => setSelectedProjectId(null)}
        projectId={selectedProjectId}
      />
    );
  }

  return (
    <Box>
      <Typography
        sx={{
          color: secondaryColors.SECONDARY,
          fontSize: '22px',
          fontWeight: 600,
          mb: 1
        }}
      >
        Active projects
      </Typography>
      <Typography
        sx={{ color: secondaryColors.SECONDARY, fontSize: '13px', mb: 3 }}
      >
        Click a project to view service details and task status.
      </Typography>

      {!projects?.length ? (
        <Box
          sx={{
            background: '#fff',
            border: `1px solid ${secondaryColors.SECONDARY_6}`,
            borderRadius: '12px',
            p: 4,
            textAlign: 'center'
          }}
        >
          <Typography
            sx={{ color: secondaryColors.SECONDARY, fontSize: '14px' }}
          >
            No active projects assigned to your company right now.
          </Typography>
        </Box>
      ) : (
        <TableCard>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledHeaderCell>Project</StyledHeaderCell>
                  <StyledHeaderCell>Client</StyledHeaderCell>
                  <StyledHeaderCell>Location</StyledHeaderCell>
                  <StyledHeaderCell>Status</StyledHeaderCell>
                  <StyledHeaderCell>Start</StyledHeaderCell>
                  <StyledHeaderCell>End</StyledHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {projects.map((project: PortalProject) => {
                  const clientName =
                    project.clientBusinessName ||
                    [project.clientFirstName, project.clientLastName]
                      .filter(Boolean)
                      .join(' ') ||
                    '—';
                  return (
                    <StyledRow
                      key={project.id}
                      onClick={() => setSelectedProjectId(project.id)}
                    >
                      <TableCell>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                          {project.leadName || clientName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: '13px' }}>
                          {clientName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            color: secondaryColors.SECONDARY,
                            fontSize: '12px',
                            maxWidth: 220,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {project.location || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusPill status={project.status} />
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: '12px' }}>
                          {formatDate(project.startDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: '12px' }}>
                          {formatDate(project.endDate)}
                        </Typography>
                      </TableCell>
                    </StyledRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </TableCard>
      )}
    </Box>
  );
};

export default PortalProjects;
